import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown, ArrowUp, ArrowUpDown, Ban, CircleCheck, CirclePause, CircleSlash, Clock, DollarSign,
  Info, Pause, Play, RefreshCw, Search, Send, ShieldAlert, TriangleAlert,
} from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import {
  addGoogleNegativeKeyword, fetchGoogleDashboard, GoogleDashboardKpis, GoogleDashboardResponse,
  GoogleDashboardRow, GoogleGeo, GoogleLevel, GoogleSearchTermRow, triggerGoogleDispatchNow,
  triggerGoogleSyncNow, updateGoogleLiveBudget, updateGoogleLiveStatus,
} from '../services/googleAdsApi';

// SimplyLoan brand: pine green for structure, warm orange only on the primary action, soft mint as wash.
const PINE = '#0F3F34';
const ORANGE = '#F07824';
const MINT = '#F4F8F6';
const STATUS = { good: '#0ca30c', warning: '#fab219', critical: '#d03b3b', neutral: '#8a8984' };
const TZ = 'Europe/Copenhagen';

type Preset = '7d' | '30d' | '90d' | 'custom';
type ViewTab = GoogleLevel | 'search_terms';
type SortKey =
  | 'name' | 'spend_dkk' | 'clicks' | 'leads' | 'cpl_dkk'
  | 'rev_d0' | 'immediate_roas' | 'rev_d30' | 'ltv30_roas' | 'rev_pending';
type SearchSortKey = 'search_term' | 'spend_dkk' | 'clicks' | 'impressions' | 'conversions' | 'cpc_dkk' | 'ctr';

const PRESET_DAYS: Record<Exclude<Preset, 'custom'>, number> = { '7d': 7, '30d': 30, '90d': 90 };
const GEOS: { id: GoogleGeo; label: string }[] = [
  { id: 'all', label: 'All' }, { id: 'se', label: 'SE' }, { id: 'no', label: 'NO' }, { id: 'fi', label: 'FI' },
];
const TABS: { id: ViewTab; label: string }[] = [
  { id: 'campaign', label: 'Campaign' },
  { id: 'adgroup', label: 'Ad group' },
  { id: 'ad', label: 'RSA Ad' },
  { id: 'search_terms', label: 'Search terms' },
];

const dkkFormat = new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 });
const intFormat = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 });
const pctFormat = new Intl.NumberFormat('da-DK', { style: 'percent', maximumFractionDigits: 1 });

const money = (v: number | null | undefined) => (v == null ? '–' : dkkFormat.format(v));
const count = (v: number | null | undefined) => (v == null ? '–' : intFormat.format(v));
const roas = (v: number | null | undefined) =>
  v == null ? '–' : `${v.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;

const isoDay = (d: Date) => d.toLocaleDateString('sv-SE', { timeZone: TZ });

function presetRange(preset: Exclude<Preset, 'custom'>) {
  const to = new Date();
  const from = new Date(to.getTime() - (PRESET_DAYS[preset] - 1) * 86_400_000);
  return { from: isoDay(from), to: isoDay(to) };
}

function describeStatus(status: string | null, policyStatus?: string | null) {
  if (policyStatus === 'DISAPPROVED') {
    return { label: 'Disapproved', color: STATUS.critical, Icon: TriangleAlert };
  }
  if (policyStatus === 'APPROVED_LIMITED') {
    return { label: 'Limited (policy)', color: STATUS.warning, Icon: ShieldAlert };
  }
  switch (status) {
    case 'ENABLED':
    case 'ACTIVE':
      return { label: 'Enabled', color: STATUS.good, Icon: CircleCheck };
    case 'PAUSED':
      return { label: 'Paused', color: STATUS.neutral, Icon: CirclePause };
    case 'UNDER_REVIEW':
    case 'PENDING_REVIEW':
      return { label: 'In review', color: STATUS.warning, Icon: Clock };
    case 'REMOVED':
      return { label: 'Removed', color: STATUS.neutral, Icon: CircleSlash };
    default:
      return null;
  }
}

function StatusPill({ status, policyStatus }: { status: string | null; policyStatus?: string | null }) {
  const s = describeStatus(status, policyStatus);
  if (!s) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600">
      <s.Icon size={12} color={s.color} aria-hidden="true" />
      {s.label}
    </span>
  );
}

function AdStrengthBadge({ strength }: { strength: string | null }) {
  if (!strength || strength === 'UNSPECIFIED' || strength === 'UNKNOWN') return null;
  const color =
    strength === 'EXCELLENT' || strength === 'GOOD'
      ? STATUS.good
      : strength === 'AVERAGE'
        ? STATUS.warning
        : STATUS.critical;
  return (
    <span className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      RSA {strength.toLowerCase()}
    </span>
  );
}

function KpiTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

function Segmented<T extends string>({ options, value, onChange, label }: {
  options: { id: T; label: string; short?: string }[]; value: T; onChange: (v: T) => void; label: string;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-pressed={value === o.id}
          aria-label={o.label}
          className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === o.id ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
          style={value === o.id ? { backgroundColor: PINE } : undefined}
        >
          {o.short ? (
            <>
              <span className="sm:hidden">{o.short}</span>
              <span className="hidden sm:inline">{o.label}</span>
            </>
          ) : o.label}
        </button>
      ))}
    </div>
  );
}

function kpiTiles(k: GoogleDashboardKpis) {
  return [
    {
      label: 'Google Ads spend',
      value: money(k.spend_dkk),
      sub: k.unmapped_spend_dkk
        ? `${money(k.unmapped_spend_dkk)} not mapped to a market`
        : `${count(k.clicks)} clicks · CPC ${money(k.cpc_dkk)}`,
    },
    {
      label: 'Leads',
      value: count(k.leads),
      sub: `Google reports ${k.google_reported_conversions.toLocaleString('da-DK', { maximumFractionDigits: 1 })} conv.`,
    },
    {
      label: 'Cost per lead',
      value: money(k.cpl_dkk),
      sub: k.ctr != null ? `CTR ${pctFormat.format(k.ctr)} · ${count(k.impressions)} impr.` : 'Spend ÷ our leads',
    },
    {
      label: 'Blended ROAS',
      value: roas(k.blended_roas),
      sub: 'All approved revenue ÷ spend',
    },
    {
      label: 'Front-end revenue',
      value: money(k.frontend_revenue_dkk),
      sub: 'Approved within 24 h of the lead',
    },
    {
      label: 'Email drip revenue',
      value: money(k.drip_revenue_dkk),
      sub: 'Approved day 1–90, to date',
    },
    {
      label: 'Pending commission',
      value: money(k.pending_revenue_dkk),
      sub: 'Reported, not yet approved',
    },
    {
      label: 'Net margin',
      value: money(k.net_margin_dkk),
      sub: k.projected_d30_revenue_dkk != null
        ? `Projected D30 revenue ${money(k.projected_d30_revenue_dkk)}`
        : 'Approved revenue − spend',
    },
  ];
}

function sortRows(rows: GoogleDashboardRow[], key: SortKey, desc: boolean) {
  const sorted = [...rows].sort((a, b) => {
    if (key === 'name') return a.name.localeCompare(b.name, 'da');
    const av = a[key] ?? Number.NEGATIVE_INFINITY;
    const bv = b[key] ?? Number.NEGATIVE_INFINITY;
    return (av as number) - (bv as number);
  });
  return desc ? sorted.reverse() : sorted;
}

function sortSearchTerms(rows: GoogleSearchTermRow[], key: SearchSortKey, desc: boolean) {
  const sorted = [...rows].sort((a, b) => {
    if (key === 'search_term') return a.search_term.localeCompare(b.search_term, 'da');
    const av = a[key] ?? Number.NEGATIVE_INFINITY;
    const bv = b[key] ?? Number.NEGATIVE_INFINITY;
    return (av as number) - (bv as number);
  });
  return desc ? sorted.reverse() : sorted;
}

const COLUMNS: { key: SortKey; label: string; title: string }[] = [
  { key: 'spend_dkk', label: 'Spend', title: 'Google Ads spend in the selected dates, DKK' },
  { key: 'clicks', label: 'Clicks', title: 'Google Ads clicks, CPC, and CTR' },
  { key: 'leads', label: 'Leads', title: 'Our leads (web form + gclid/gbraid/wbraid click-outs)' },
  { key: 'cpl_dkk', label: 'CPL', title: 'Spend divided by our leads' },
  { key: 'rev_d0', label: 'D0 revenue', title: 'Approved commission within 24 hours of the lead' },
  { key: 'immediate_roas', label: 'Immediate ROAS', title: 'D0 revenue divided by spend' },
  { key: 'rev_d30', label: 'D30 revenue', title: 'Approved commission within 30 days of the lead, to date' },
  { key: 'ltv30_roas', label: '30-day LTV ROAS', title: 'D30 revenue divided by spend; projection uses matured cohorts' },
  { key: 'rev_pending', label: 'Pending', title: 'Commission reported but not yet approved' },
];

function synced(value: string | null | undefined) {
  if (!value) return 'never';
  return new Date(value).toLocaleString('da-DK', { timeZone: TZ, dateStyle: 'short', timeStyle: 'short' });
}

function HealthStrip({ data }: { data: GoogleDashboardResponse }) {
  const conv = data.health.conversions_24h;
  const sent = (conv.by_status.sent ?? 0) + (conv.by_status.validate_only ?? 0);
  const dryRun = conv.by_status.dry_run ?? 0;
  const problems = conv.auth_errors > 0 || (conv.error_rate ?? 0) > 0.02 || conv.backlog_over_60m > 0;
  const Icon = problems ? TriangleAlert : Info;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-gray-200 px-4 py-2 text-xs text-gray-600" style={{ backgroundColor: MINT }}>
      <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
        <Icon size={14} color={problems ? STATUS.critical : PINE} aria-hidden="true" />
        Offline & Enhanced Conversions, last 24 h
      </span>
      <span>uploaded {count(sent)}{dryRun ? ` · dry run ${count(dryRun)}` : ''}</span>
      <span>failed {count(conv.by_status.failed ?? 0)}{conv.error_rate != null ? ` (${pctFormat.format(conv.error_rate)})` : ''}</span>
      <span>gclid/braid on {conv.gclid_match_share != null ? pctFormat.format(conv.gclid_match_share) : '–'}</span>
      <span>email/phone EC on {conv.ec_match_share != null ? pctFormat.format(conv.ec_match_share) : '–'}</span>
      {conv.backlog_over_60m > 0 && <span>{count(conv.backlog_over_60m)} waiting over 60 min</span>}
      {conv.auth_errors > 0 && <span>{count(conv.auth_errors)} OAuth/token errors</span>}
      <span className="ml-auto">GAQL synced {synced(data.health.synced_at.insights_sync)}</span>
    </div>
  );
}

export default function SimplyloanGoogleDashboard() {
  const queryClient = useQueryClient();
  const [preset, setPreset] = useState<Preset>('30d');
  const [custom, setCustom] = useState(() => presetRange('30d'));
  const [geo, setGeo] = useState<GoogleGeo>('all');
  const [tab, setTab] = useState<ViewTab>('campaign');
  const [sortKey, setSortKey] = useState<SortKey>('spend_dkk');
  const [sortDesc, setSortDesc] = useState(true);
  const [searchSortKey, setSearchSortKey] = useState<SearchSortKey>('spend_dkk');
  const [searchSortDesc, setSearchSortDesc] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [wastedOnly, setWastedOnly] = useState(false);
  const [negMatchType, setNegMatchType] = useState<'EXACT' | 'PHRASE'>('EXACT');
  const [negScope, setNegScope] = useState<'campaign' | 'adgroup'>('campaign');
  const [notice, setNotice] = useState<string | null>(null);

  const apiLevel: GoogleLevel = tab === 'search_terms' ? 'campaign' : tab;
  const range = preset === 'custom' ? custom : presetRange(preset);

  const query = useQuery({
    queryKey: ['simplyloan-google-dashboard', geo, apiLevel, range.from, range.to],
    queryFn: ({ signal }) => fetchGoogleDashboard({ geo, level: apiLevel, from: range.from, to: range.to }, signal),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });

  const data = query.data;
  const rows = useMemo(() => (data ? sortRows(data.rows, sortKey, sortDesc) : []), [data, sortKey, sortDesc]);

  const searchTerms = useMemo(() => {
    if (!data) return [];
    let list = data.search_terms;
    if (wastedOnly) {
      list = list.filter((s) => s.conversions <= 0 && s.spend_dkk > 0);
    }
    if (searchFilter.trim()) {
      const q = searchFilter.trim().toLowerCase();
      list = list.filter(
        (s) =>
          s.search_term.toLowerCase().includes(q) ||
          (s.campaign_name || '').toLowerCase().includes(q) ||
          (s.adgroup_name || '').toLowerCase().includes(q) ||
          (s.keyword_text || '').toLowerCase().includes(q),
      );
    }
    return sortSearchTerms(list, searchSortKey, searchSortDesc);
  }, [data, wastedOnly, searchFilter, searchSortKey, searchSortDesc]);

  const statusMut = useMutation({
    mutationFn: updateGoogleLiveStatus,
    onSuccess: (res) => {
      setNotice(
        res.status === 'ok'
          ? `Updated ${res.object_type} ${res.object_id} → ${res.new_status}.`
          : res.reason || res.error || `Status response: ${res.status}`,
      );
      queryClient.invalidateQueries({ queryKey: ['simplyloan-google-dashboard'] });
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const budgetMut = useMutation({
    mutationFn: updateGoogleLiveBudget,
    onSuccess: (res) => {
      setNotice(
        res.status === 'ok'
          ? `Updated campaign ${res.campaign_id} daily budget to ${res.daily_budget}.`
          : res.reason || res.error || `Budget response: ${res.status}`,
      );
      queryClient.invalidateQueries({ queryKey: ['simplyloan-google-dashboard'] });
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const negMut = useMutation({
    mutationFn: addGoogleNegativeKeyword,
    onSuccess: (res) => {
      setNotice(
        res.status === 'ok'
          ? `Added negative keyword "${res.keyword}" (${res.match_type}).`
          : res.reason || res.error || `Negative keyword response: ${res.status}`,
      );
      queryClient.invalidateQueries({ queryKey: ['simplyloan-google-dashboard'] });
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const syncMut = useMutation({
    mutationFn: () => triggerGoogleSyncNow(7),
    onSuccess: () => {
      setNotice('GAQL Insights & Search Terms sync completed.');
      queryClient.invalidateQueries({ queryKey: ['simplyloan-google-dashboard'] });
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const dispatchMut = useMutation({
    mutationFn: () => triggerGoogleDispatchNow(),
    onSuccess: () => {
      setNotice('Offline & Enhanced Conversions outbox dispatched.');
      queryClient.invalidateQueries({ queryKey: ['simplyloan-google-dashboard'] });
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const onSort = (key: SortKey) => {
    if (key === sortKey) setSortDesc(!sortDesc);
    else {
      setSortKey(key);
      setSortDesc(key !== 'name' && key !== 'cpl_dkk');
    }
  };

  const onSearchSort = (key: SearchSortKey) => {
    if (key === searchSortKey) setSearchSortDesc(!searchSortDesc);
    else {
      setSearchSortKey(key);
      setSearchSortDesc(key !== 'search_term');
    }
  };

  const handleToggleStatus = (r: GoogleDashboardRow) => {
    if (!r.key || (tab !== 'campaign' && tab !== 'adgroup')) return;
    const nextStatus = r.status === 'ENABLED' ? 'PAUSED' : 'ENABLED';
    const label = nextStatus === 'PAUSED' ? 'Pause' : 'Enable';
    if (!window.confirm(`${label} ${tab} "${r.name}" (${r.key})?`)) return;
    statusMut.mutate({
      geo: r.geo || (geo !== 'all' ? geo : 'se'),
      customer_id: r.customer_id || undefined,
      object_type: tab,
      object_id: r.key,
      status: nextStatus,
    });
  };

  const handleEditBudget = (r: GoogleDashboardRow) => {
    const campaignId = tab === 'campaign' ? r.key : r.campaign_id;
    if (!campaignId) return;
    const current = r.daily_budget_account != null ? String(Math.round(r.daily_budget_account)) : '';
    const input = window.prompt(`New daily budget (in account currency, e.g. SEK/NOK/DKK) for "${r.campaign_name || r.name}":`, current);
    if (!input) return;
    const amount = Number(input.replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) {
      setNotice('Please enter a valid positive number for daily budget.');
      return;
    }
    budgetMut.mutate({
      geo: r.geo || (geo !== 'all' ? geo : 'se'),
      customer_id: r.customer_id || undefined,
      campaign_id: campaignId,
      daily_budget: amount,
    });
  };

  const handleExcludeTerm = (st: GoogleSearchTermRow) => {
    addGoogleNegativeKeyword({
      geo: st.geo || (geo !== 'all' ? geo : 'se'),
      customer_id: st.customer_id || undefined,
      campaign_id: negScope === 'campaign' ? st.campaign_id : undefined,
      adgroup_id: negScope === 'adgroup' ? st.adgroup_id : undefined,
      keyword_text: st.search_term,
      match_type: negMatchType,
    }).then((res) => {
      setNotice(
        res.status === 'ok'
          ? `Excluded "${st.search_term}" (${negMatchType}) at ${negScope} level.`
          : res.reason || res.error || `Negative keyword response: ${res.status}`,
      );
      queryClient.invalidateQueries({ queryKey: ['simplyloan-google-dashboard'] });
    }).catch((err: Error) => setNotice(err.message));
  };

  const levelLabel = TABS.find((t) => t.id === tab)?.label ?? 'Campaign';
  const busy = statusMut.isPending || budgetMut.isPending || negMut.isPending || syncMut.isPending || dispatchMut.isPending;

  return (
    <MainLayout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: PINE }}>Google Ads · SimplyLoan</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Full-funnel Search & RSA control: cohort D0–D90 revenue, Offline gclid + Enhanced Conversions, live status/budgets, and Search Terms negative mining. All amounts in DKK.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {data && <p className="text-xs text-gray-400">Updated {synced(data.generated_at)}</p>}
            <button
              type="button"
              disabled={busy}
              onClick={() => syncMut.mutate()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              title="Run GAQL Insights & Search Terms sync now"
            >
              <RefreshCw size={12} className={syncMut.isPending ? 'animate-spin' : ''} aria-hidden="true" />
              Sync GAQL
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => dispatchMut.mutate()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              title="Collect & upload queued Offline Click / Enhanced Conversions now"
            >
              <Send size={12} aria-hidden="true" />
              Flush Conversions
            </button>
            <Link
              to="/simplyloan/google/campaigns"
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Campaign builder
            </Link>
          </div>
        </div>

        {notice && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700" style={{ backgroundColor: MINT }}>
            <span className="inline-flex items-center gap-2">
              <Info size={15} color={PINE} className="shrink-0" aria-hidden="true" />
              {notice}
            </span>
            <button type="button" onClick={() => setNotice(null)} className="text-xs text-gray-500 hover:text-gray-800">
              Dismiss
            </button>
          </div>
        )}

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2">
          <Segmented
            label="Date range"
            value={preset}
            onChange={(p) => setPreset(p)}
            options={[
              { id: '7d', label: 'Last 7 days', short: '7 d' },
              { id: '30d', label: 'Last 30 days', short: '30 d' },
              { id: '90d', label: 'Last 90 days', short: '90 d' },
              { id: 'custom', label: 'Custom' },
            ]}
          />
          {preset === 'custom' && (
            <div className="flex items-center gap-1.5">
              <input
                type="date" aria-label="From" value={custom.from} max={custom.to}
                onChange={(e) => e.target.value && setCustom({ ...custom, from: e.target.value })}
                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm"
              />
              <span className="text-sm text-gray-400">→</span>
              <input
                type="date" aria-label="To" value={custom.to} min={custom.from}
                onChange={(e) => e.target.value && setCustom({ ...custom, to: e.target.value })}
                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm"
              />
            </div>
          )}
          <Segmented label="Market" value={geo} onChange={setGeo} options={GEOS} />
          <Segmented label="View" value={tab} onChange={setTab} options={TABS} />
          <button
            type="button"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: ORANGE }}
          >
            <RefreshCw size={14} className={query.isFetching ? 'animate-spin' : ''} aria-hidden="true" />
            Refresh
          </button>
        </div>

        {query.isError && (
          <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
            <TriangleAlert size={16} color={STATUS.critical} className="mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-medium">Could not load the Google Ads dashboard.</p>
              <p className="text-gray-500">{(query.error as Error).message}</p>
            </div>
          </div>
        )}

        {!data && query.isPending && (
          <div className="flex h-48 items-center justify-center text-sm text-gray-400">Loading Google Ads performance…</div>
        )}

        {data && (
          <div className={`space-y-5 transition-opacity ${query.isFetching ? 'opacity-60' : 'opacity-100'}`}>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {kpiTiles(data.kpis).map((t) => <KpiTile key={t.label} {...t} />)}
            </div>

            <HealthStrip data={data} />

            {data.kpis.fx_missing && (
              <p className="flex items-center gap-1.5 text-xs text-gray-600">
                <TriangleAlert size={14} color={STATUS.warning} aria-hidden="true" />
                Some spend has no exchange rate yet and counts as 0 DKK. Click &ldquo;Sync GAQL&rdquo; to refresh FX rates.
              </p>
            )}

            {tab !== 'search_terms' ? (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                  <h2 className="text-sm font-semibold" style={{ color: PINE }}>{levelLabel} cohort performance</h2>
                  <p className="text-xs text-gray-500">{count(rows.length)} rows · {data.filters.from} to {data.filters.to}</p>
                </div>
                {rows.length === 0 ? (
                  <div className="px-4 py-12 text-center text-sm text-gray-500">
                    No Google Ads spend or Google-acquired leads in this range.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead style={{ backgroundColor: MINT }}>
                        <tr className="text-left text-xs font-medium text-gray-600">
                          <th scope="col" className="px-4 py-2.5">
                            <button type="button" onClick={() => onSort('name')} className="inline-flex items-center gap-1">
                              {levelLabel}
                              {sortKey === 'name' ? (sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                            </button>
                          </th>
                          {COLUMNS.map((c) => (
                            <th key={c.key} scope="col" className="whitespace-nowrap px-3 py-2.5 text-right" title={c.title}
                                aria-sort={sortKey === c.key ? (sortDesc ? 'descending' : 'ascending') : 'none'}>
                              <button type="button" onClick={() => onSort(c.key)} className="inline-flex items-center gap-1">
                                {c.label}
                                {sortKey === c.key ? (sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                              </button>
                            </th>
                          ))}
                          {(tab === 'campaign' || tab === 'adgroup') && (
                            <th scope="col" className="whitespace-nowrap px-3 py-2.5 text-right">Controls</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 tabular-nums">
                        {rows.map((r) => (
                          <tr key={r.key ?? 'unattributed'} className="hover:bg-gray-50">
                            <td className="max-w-[24rem] px-4 py-2.5">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="truncate font-medium text-gray-900" title={r.name}>{r.name}</span>
                                <StatusPill status={r.status} policyStatus={r.policy_approval_status} />
                                <AdStrengthBadge strength={r.ad_strength} />
                                {r.geo && <span className="text-[11px] font-medium uppercase text-gray-400">{r.geo}</span>}
                              </div>
                              {tab !== 'campaign' && r.campaign_name && (
                                <p className="truncate text-xs text-gray-400" title={r.campaign_name}>
                                  {tab === 'ad' && r.adgroup_name ? `${r.campaign_name} › ${r.adgroup_name}` : r.campaign_name}
                                </p>
                              )}
                              {tab === 'campaign' && (r.channel_type || r.bidding_strategy_type || r.daily_budget_account != null) && (
                                <p className="mt-0.5 text-[11px] text-gray-400">
                                  {[
                                    r.channel_type,
                                    r.bidding_strategy_type,
                                    r.daily_budget_account != null ? `Budget ${Math.round(r.daily_budget_account).toLocaleString('da-DK')}/d` : null,
                                    r.search_impression_share != null ? `IS ${pctFormat.format(r.search_impression_share)}` : null,
                                  ].filter(Boolean).join(' · ')}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-900">{money(r.spend_dkk)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-900">
                              {count(r.clicks)}
                              <span className="block text-[11px] text-gray-400">
                                {r.cpc_dkk != null ? `${money(r.cpc_dkk)} CPC` : ''}
                                {r.ctr != null ? ` · ${pctFormat.format(r.ctr)}` : ''}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-900">
                              {count(r.leads)}
                              {r.google_conversions > 0 && (
                                <span className="block text-[11px] text-gray-400">
                                  {r.google_conversions.toLocaleString('da-DK', { maximumFractionDigits: 1 })} GAds
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-900">{money(r.cpl_dkk)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-900">{money(r.rev_d0)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-900">{roas(r.immediate_roas)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-900">
                              {money(r.rev_d30)}
                              {r.leads > 0 && (
                                <span className="block text-[11px] text-gray-400">{count(r.leads_mature_d30)}/{count(r.leads)} leads 30 d old</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-900">
                              {roas(r.ltv30_roas)}
                              {r.projected_ltv30_roas != null && r.leads_mature_d30 < r.leads && (
                                <span className="block text-[11px] text-gray-400">projected {roas(r.projected_ltv30_roas)}</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-500">{money(r.rev_pending)}</td>
                            {(tab === 'campaign' || tab === 'adgroup') && (
                              <td className="whitespace-nowrap px-3 py-2.5 text-right">
                                {r.key && (
                                  <div className="inline-flex items-center gap-1">
                                    <button
                                      type="button"
                                      disabled={busy}
                                      onClick={() => handleToggleStatus(r)}
                                      className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                      title={r.status === 'ENABLED' ? 'Pause in Google Ads' : 'Enable in Google Ads'}
                                    >
                                      {r.status === 'ENABLED' ? <Pause size={11} /> : <Play size={11} />}
                                      {r.status === 'ENABLED' ? 'Pause' : 'Enable'}
                                    </button>
                                    {tab === 'campaign' && (
                                      <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => handleEditBudget(r)}
                                        className="inline-flex items-center gap-0.5 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                        title="Update daily budget in Google Ads"
                                      >
                                        <DollarSign size={11} />
                                        Budget
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t border-gray-200 font-medium tabular-nums" style={{ backgroundColor: MINT }}>
                        <tr>
                          <td className="px-4 py-2.5 text-gray-700">Total</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{money(data.kpis.spend_dkk)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{count(data.kpis.clicks)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{count(data.kpis.leads)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{money(data.kpis.cpl_dkk)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{money(data.kpis.frontend_revenue_dkk)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">
                            {roas(data.kpis.spend_dkk ? data.kpis.frontend_revenue_dkk / data.kpis.spend_dkk : null)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-900">{money(rows.reduce((s, r) => s + r.rev_d30, 0))}</td>
                          <td className="px-3 py-2.5 text-right text-gray-900">
                            {roas(data.kpis.spend_dkk ? rows.reduce((s, r) => s + r.rev_d30, 0) / data.kpis.spend_dkk : null)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-500">{money(data.kpis.pending_revenue_dkk)}</td>
                          {(tab === 'campaign' || tab === 'adgroup') && <td />}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              /* Search Terms Mining & 1-Click Negative Keyword View */
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                  <div>
                    <h2 className="text-sm font-semibold" style={{ color: PINE }}>Search terms & negative keyword mining</h2>
                    <p className="text-xs text-gray-500">
                      Actual user search queries from Google Ads. Exclude non-converting queries in one click.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <div className="relative">
                      <Search size={13} className="pointer-events-none absolute left-2.5 top-2 text-gray-400" />
                      <input
                        type="search"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        placeholder="Filter query, campaign, keyword…"
                        className="rounded-lg border border-gray-200 py-1 pl-7 pr-2.5 text-xs focus:outline-none"
                      />
                    </div>
                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-gray-600 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={wastedOnly}
                        onChange={(e) => setWastedOnly(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      0 conversions only
                    </label>
                    <select
                      value={negMatchType}
                      onChange={(e) => setNegMatchType(e.target.value as 'EXACT' | 'PHRASE')}
                      aria-label="Negative keyword match type"
                      className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700"
                    >
                      <option value="EXACT">Negative Exact [term]</option>
                      <option value="PHRASE">Negative Phrase &ldquo;term&rdquo;</option>
                    </select>
                    <select
                      value={negScope}
                      onChange={(e) => setNegScope(e.target.value as 'campaign' | 'adgroup')}
                      aria-label="Negative keyword scope"
                      className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700"
                    >
                      <option value="campaign">Campaign level</option>
                      <option value="adgroup">Ad group level</option>
                    </select>
                  </div>
                </div>
                {searchTerms.length === 0 ? (
                  <div className="px-4 py-12 text-center text-sm text-gray-500">
                    No search terms match the selected filters.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead style={{ backgroundColor: MINT }}>
                        <tr className="text-left text-xs font-medium text-gray-600">
                          <th scope="col" className="px-4 py-2.5">
                            <button type="button" onClick={() => onSearchSort('search_term')} className="inline-flex items-center gap-1">
                              Search term
                              {searchSortKey === 'search_term' ? (searchSortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                            </button>
                          </th>
                          <th scope="col" className="px-3 py-2.5">Matched keyword</th>
                          <th scope="col" className="px-3 py-2.5 text-right">
                            <button type="button" onClick={() => onSearchSort('impressions')} className="inline-flex items-center gap-1">
                              Impr.
                              {searchSortKey === 'impressions' ? (searchSortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                            </button>
                          </th>
                          <th scope="col" className="px-3 py-2.5 text-right">
                            <button type="button" onClick={() => onSearchSort('clicks')} className="inline-flex items-center gap-1">
                              Clicks
                              {searchSortKey === 'clicks' ? (searchSortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                            </button>
                          </th>
                          <th scope="col" className="px-3 py-2.5 text-right">
                            <button type="button" onClick={() => onSearchSort('cpc_dkk')} className="inline-flex items-center gap-1">
                              CPC
                              {searchSortKey === 'cpc_dkk' ? (searchSortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                            </button>
                          </th>
                          <th scope="col" className="px-3 py-2.5 text-right">
                            <button type="button" onClick={() => onSearchSort('spend_dkk')} className="inline-flex items-center gap-1">
                              Spend
                              {searchSortKey === 'spend_dkk' ? (searchSortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                            </button>
                          </th>
                          <th scope="col" className="px-3 py-2.5 text-right">
                            <button type="button" onClick={() => onSearchSort('conversions')} className="inline-flex items-center gap-1">
                              Conv.
                              {searchSortKey === 'conversions' ? (searchSortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} className="text-gray-300" />}
                            </button>
                          </th>
                          <th scope="col" className="px-4 py-2.5 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 tabular-nums">
                        {searchTerms.map((st) => (
                          <tr key={`${st.adgroup_id}:${st.search_term}`} className="hover:bg-gray-50">
                            <td className="max-w-[22rem] px-4 py-2.5">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-medium text-gray-900">{st.search_term}</span>
                                {st.search_term_status && st.search_term_status !== 'NONE' && (
                                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                    {st.search_term_status}
                                  </span>
                                )}
                                {st.geo && <span className="text-[11px] font-medium uppercase text-gray-400">{st.geo}</span>}
                              </div>
                              <p className="truncate text-xs text-gray-400" title={`${st.campaign_name} › ${st.adgroup_name}`}>
                                {st.campaign_name} › {st.adgroup_name}
                              </p>
                            </td>
                            <td className="px-3 py-2.5 text-xs text-gray-600">
                              {st.keyword_text || '–'}
                              {st.keyword_match_type && (
                                <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                                  {st.keyword_match_type}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-900">{count(st.impressions)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-900">
                              {count(st.clicks)}
                              {st.ctr != null && <span className="block text-[11px] text-gray-400">{pctFormat.format(st.ctr)}</span>}
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-900">{money(st.cpc_dkk)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-900">{money(st.spend_dkk)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-900">
                              {st.conversions.toLocaleString('da-DK', { maximumFractionDigits: 1 })}
                              {st.cpa_dkk != null && <span className="block text-[11px] text-gray-400">CPA {money(st.cpa_dkk)}</span>}
                            </td>
                            <td className="whitespace-nowrap px-4 py-2.5 text-right">
                              {st.search_term_status === 'EXCLUDED' ? (
                                <span className="text-xs text-gray-400">Excluded</span>
                              ) : (
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => handleExcludeTerm(st)}
                                  className="inline-flex items-center gap-1 rounded border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                  title={`Add negative ${negMatchType.toLowerCase()} keyword at ${negScope} level`}
                                >
                                  <Ban size={12} color={STATUS.critical} aria-hidden="true" />
                                  Add negative
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
